/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Security } from '@okta/okta-react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsLoginConfigLoaded, selectLoginConfig, selectShouldSignOut, selectIsAuthenticated } from '../../store';
import React, { useEffect } from 'react';
import { getLoginConfig } from '../../store/config/actions';
import { useHistory } from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LOGIN_REDIRECT_URI, LOGIN_ROUTE, SCOPES_REQUIRED } from '../../utils/AuthUtils';
import { setAuthTokens, signOut } from '../../store/session/actions';
import { isMockMode } from '../../utils/GlobalUtils';

interface SecurityProviderProps {}

/**
 * SecurityProvider is a component that wraps other components and permits them to use the
 * useOktaAuth context provider. The component will not render it's child components until
 * it is able to provide all necessary config for the Okta auth context. When running in
 * mock mode, this component effectively does nothing as it will always render the child
 * components.
 */
const SecurityProvider: React.FunctionComponent<SecurityProviderProps> = props => {
  const dispatch = useDispatch();
  const history = useHistory();
  const insightsIsAuthenticated = useSelector(selectIsAuthenticated);

  // Start retrieval of login/auth config
  useEffect(() => {
    dispatch(getLoginConfig());
  }, [dispatch]);

  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
    history.replace(toRelativeUrl(originalUri || '', window.location.origin));
  };

  const authRequiredHandler = () => {
    // Redirect to login route when not logged in
    history.replace(LOGIN_ROUTE);
  };

  const isLoginConfigLoaded = useSelector(selectIsLoginConfigLoaded);
  const loginConfig = useSelector(selectLoginConfig);
  const shouldSignOut = useSelector(selectShouldSignOut);

  let oktaAuth: OktaAuth | undefined = undefined;
  if (isLoginConfigLoaded && !isMockMode()) {
    oktaAuth = new OktaAuth({
      issuer: loginConfig.oidcIssuer,
      clientId: loginConfig.oidcClientId,
      redirectUri: LOGIN_REDIRECT_URI,
      scopes: SCOPES_REQUIRED,

      // Uncomment the line below for additional logging in the dev console.
      // It's useful for debugging issues with auto token re-auth.
      // devMode: true
    });

    // Sign out the user if Okta tells us a login is required in response
    // to a token renewal request. This means the users' session has
    // expired and they must log back in.
    oktaAuth.tokenManager.on('error', error => {
      if (error.errorCode === 'login_required') {
        dispatch(signOut());
      }
    });

    // Dispatch setAuthTokens if the auth state manager
    // has valid tokens after the page has loaded. This
    // occurs when Okta refreshes tokens in the background
    // 30s before they would expire.
    oktaAuth.authStateManager.subscribe(authState => {
      // if Okta is authenticated, sync insights redux store with
      // Okta sign-in state and tokens
      if (authState.isAuthenticated) {
        dispatch(
          setAuthTokens({
            accessToken: authState.accessToken,
            idToken: authState.idToken,
          }),
        );
      }
      // if Okta is not authenticated, but insights is authenticated
      // sync insights redux store to sign out.
      else if (insightsIsAuthenticated) {
        dispatch(signOut());
      }
    });

    // shouldSignOut is set when the SignOut action is triggered
    // on the session store. This is only needed so that the rest
    // of the app can trigger an Okta-sign-out from other locations
    // in the app, without needing the Okta context.
    if (shouldSignOut) {
      oktaAuth.signOut().then(() => {
        // Clear tokens so that the user stays logged out.
        oktaAuth?.clearStorage();
      })
    }
  }

  // When not in mock mode, if the Okta context:
  //    * Exists: Display child elements.
  //    * Does not exist: Don't display child elements.
  // When in mock mode: always display child elements.
  return oktaAuth ? (
    <Security oktaAuth={oktaAuth} onAuthRequired={authRequiredHandler} restoreOriginalUri={restoreOriginalUri}>
      {props.children}
    </Security>
  ) : (
    <>{isMockMode() && props.children}</>
  );
};

export default SecurityProvider;